// Translated UI wrapper for PHASE_TEXT from common/constants.
// Worker imports PHASE_TEXT directly from common/constants.
//
// Uses a Proxy so each property access calls i18n.t at access time, not at
// module load time. This avoids two problems:
// 1) Accessing PHASE_TEXT at module top level (e.g. in NewLeague/index.tsx's
//    `const phases = [...]`) would otherwise run before initI18n() finishes
//    and yield undefined from i18next.
// 2) Falling back to the untranslated base value when i18n returns falsy.
import { PHASE_TEXT as PHASE_TEXT_BASE } from "../../../../common/constants.ts";
import i18n from "./i18n.ts";

type PhaseTextKey = keyof typeof PHASE_TEXT_BASE;

const getPhaseTranslationKey = (key: string, base: string) =>
	key === "4" && base === "before draft" ? "phase.4BeforeDraft" : `phase.${key}`;

const translatePhaseValue = (base: string) => {
	if (!i18n.isInitialized) {
		return base;
	}

	if (base === "after playoffs") {
		const translated = i18n.t("phase.afterPlayoffs", {
			ns: "ui",
			defaultValue: base,
		});
		return typeof translated === "string" && translated.length > 0
			? translated
			: base;
	}

	for (const [key, value] of Object.entries(PHASE_TEXT_BASE)) {
		if (value !== base) {
			continue;
		}

		const translated = i18n.t(getPhaseTranslationKey(key, base), {
			ns: "ui",
			defaultValue: base,
		});
		return typeof translated === "string" && translated.length > 0
			? translated
			: base;
	}

	return base;
};

export const translatePhaseText = (phaseText: string) => {
	const match = phaseText.match(/^(\d+)\s+(.+)$/);
	if (!match) {
		return translatePhaseValue(phaseText);
	}

	return `${match[1]} ${translatePhaseValue(match[2]!)}`;
};

export const PHASE_TEXT = new Proxy(PHASE_TEXT_BASE, {
	get(target, key) {
		const base = target[key as PhaseTextKey];
		if (typeof base !== "string") {
			return base;
		}
		if (!i18n.isInitialized) {
			return base;
		}
		const keyString = String(key);
		const translated = i18n.t(getPhaseTranslationKey(keyString, base), {
			ns: "ui",
			defaultValue: base,
		});
		return typeof translated === "string" && translated.length > 0
			? translated
			: base;
	},
});
